/**
 * Compose-with-AI: the client half.
 *
 * THE ONE IDEA TO HOLD ON TO: the generate endpoint PROPOSES and creates
 * nothing. What comes back is a list of drafts the user reviews, edits and
 * deselects; the survivors are written one by one through the ordinary
 * `v2Api.createIssue`, so issue-key allocation, board placement and the
 * activity log all keep happening in the single place that already does them.
 *
 * The capability flag matters as much as the endpoint. There is no API key:
 * each WORKSPACE connects its own Claude subscription, and the session payload
 * reports a boolean per workspace. The UI hides the entry point when it is
 * false — a button that always errors is worse than no button — and shows an
 * admin the way to switch it on instead.
 */

import { useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError, NetworkError, api } from '@/lib/api'
import type { UUID } from '@/types/api'

import { v2Api } from './api'
import { issueKeys } from './keys'
import type { Issue, IssueType, IssueWriteInput, TaskPriority } from './types'
import { invalidateIssueLists } from './useIssueMutations'
import { useWorkspace } from './useWorkspace'

/** Bounds the server enforces; mirrored here so the form cannot ask for more. */
export const AI_MAX_COUNT = 15
export const AI_DEFAULT_COUNT = 5
export const AI_MAX_PROMPT = 2000
export const AI_MIN_PROMPT = 8

/** One draft issue. Nothing here exists in the database yet. */
export interface ProposedIssue {
  title: string
  description: string
  issue_type: IssueType
  priority: TaskPriority
  story_points: number | null
  labels: string[]
}

export interface GenerateIssuesResponse {
  issues: ProposedIssue[]
  model: string
}

export interface GenerateIssuesInput {
  prompt: string
  count?: number
  sprint_id?: UUID
}

/**
 * Machine codes the server puts on the error envelope. Branching on these
 * rather than on prose is what lets each failure get an honest, specific
 * message instead of one shrug that covers four different situations.
 */
export const AI_CODES = {
  /** The deployment has no AI wiring at all. Nobody here can fix that. */
  notConfigured: 'ai_not_configured',
  /** THIS workspace has not signed in to Claude. An admin can fix it. */
  notConnected: 'ai_not_connected',
  /** It did, and the grant is no longer accepted. Back to the connect step. */
  reconnectRequired: 'ai_reconnect_required',
  refused: 'ai_refused',
  rateLimited: 'ai_rate_limited',
  unavailable: 'ai_unavailable',
} as const

/**
 * True when the failure means "go and (re)connect Claude".
 *
 * The compose modal branches on it to offer a link to settings instead of a
 * dead end, and the board uses the same rule to decide what to render.
 */
export function isConnectionError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  return error.code === AI_CODES.notConnected || error.code === AI_CODES.reconnectRequired
}

export const aiApi = {
  generateIssues: (
    slug: string,
    projectKey: string,
    input: GenerateIssuesInput,
    signal?: AbortSignal,
  ) =>
    api.post<GenerateIssuesResponse>(
      `/api/v2/orgs/${encodeURIComponent(slug)}/projects/${encodeURIComponent(projectKey)}/issues/generate`,
      input,
      { signal, headers: { Accept: 'text/event-stream' } },
    ),
}

/** Roles that may connect or disconnect a workspace's Claude account. */
const AI_ADMIN_ROLES: readonly string[] = ['owner', 'admin']

/** What the board and backlog need to know to render the AI entry point. */
export interface AiConnection {
  /** This workspace has a Claude account connected. */
  connected: boolean
  /** The current user could connect one if it does not. */
  canManage: boolean
  /** Link target for the connect prompt, or null before the slug resolves. */
  settingsPath: string | null
}

/**
 * Whether the CURRENT WORKSPACE can draft with AI, and what to offer if not.
 *
 * It reads the session the app already loads, so there is no extra round trip
 * and no flash of a button that then disappears. The answer comes from the
 * membership for the workspace in the URL rather than from a deployment-wide
 * flag, because the credential is per workspace and the app routes by slug.
 *
 * While the session is still loading the answer is "not connected, nothing to
 * offer": rendering nothing and then revealing the button is better than the
 * reverse.
 */
export function useAiConnection(): AiConnection {
  const { organization, slug } = useWorkspace()
  return {
    connected: organization?.ai_connected === true,
    canManage: AI_ADMIN_ROLES.includes(organization?.role ?? ''),
    settingsPath: slug ? `/${slug}/settings/ai` : null,
  }
}

/** Prompt examples. Concrete on purpose: an empty textarea gets empty briefs. */
export const AI_EXAMPLE_PROMPTS: readonly string[] = [
  'Harden the sign-in flow before the pilot: rate limiting, lockout after repeated failures, and an audit trail admins can read.',
  'Break down "let people export a project to CSV" into the API work, the UI work and the tests.',
  'We keep losing edits when the iPad goes offline. Draft the work to find the cause and fix it.',
]

/**
 * A generation in flight.
 *
 * The call takes several seconds, so cancellation is not optional: the modal's
 * Cancel button aborts the request through an AbortController rather than
 * leaving it running and throwing the answer away. An abort is not an error —
 * `error` stays null so the UI returns to the prompt step cleanly.
 */
export function useGenerateIssues(slug: string | null, projectKey: string | null) {
  const controllerRef = useRef<AbortController | null>(null)
  const client = useQueryClient()

  const mutation = useMutation<GenerateIssuesResponse, unknown, GenerateIssuesInput>({
    mutationFn: (input) => {
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      return aiApi.generateIssues(slug as string, projectKey as string, input, controller.signal)
    },
    onError: (error) => {
      // The workspace's sign-in died since this screen loaded. Re-read the
      // session so the board stops offering a button that cannot work and
      // starts offering the way to fix it instead.
      if (isConnectionError(error)) {
        void client.invalidateQueries({ queryKey: issueKeys.session })
        void client.invalidateQueries({ queryKey: ['session'] })
      }
    },
    onSettled: () => {
      controllerRef.current = null
    },
  })

  const cancel = () => {
    controllerRef.current?.abort()
    controllerRef.current = null
    mutation.reset()
  }

  // An AbortError surfaces as a DOMException, not an ApiError. Treating it as
  // a failure would show "Something went wrong" to someone who just clicked
  // Cancel.
  const error = isAbort(mutation.error) ? null : mutation.error

  return { ...mutation, error, cancel }
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/** One honest sentence per failure mode. */
export function aiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case AI_CODES.notConfigured:
        return 'Drafting with AI is not switched on for this Taskly deployment.'
      case AI_CODES.notConnected:
      case AI_CODES.reconnectRequired:
        // The server's own message already names the next action and knows
        // whether this person can take it; anything invented here would be
        // vaguer than what it said.
        return error.message
      case AI_CODES.refused:
        // The server passes the model's own explanation through; it is more
        // useful than anything this file could invent.
        return error.message
      case AI_CODES.rateLimited:
        return error.retryAfter
          ? `You have drafted a lot just now. Try again in about ${formatWait(error.retryAfter)}.`
          : 'You have drafted a lot just now. Try again shortly.'
      case AI_CODES.unavailable:
        return error.message
      default:
        break
    }
    if (error.status === 429) return 'Too many requests just now. Try again shortly.'
    if (error.status === 403) return 'You have read-only access to this workspace.'
    return error.message
  }
  if (error instanceof NetworkError) {
    return 'Could not reach Taskly. Check your connection and try again.'
  }
  if (error instanceof Error) return error.message
  return 'Drafting failed. Try again.'
}

function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`
  const minutes = Math.ceil(seconds / 60)
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

/** A proposal plus the review state the user drives. */
export interface DraftIssue extends ProposedIssue {
  /** Stable across edits, so React keys survive a title change. */
  key: string
  selected: boolean
}

export function toDrafts(issues: ProposedIssue[]): DraftIssue[] {
  return issues.map((issue, index) => ({
    ...issue,
    // Titles can repeat once the user edits them, so the index is the identity.
    key: `draft-${index}`,
    // Everything is selected by default: the common case is "yes, all of these".
    selected: true,
  }))
}

export interface CommitProgress {
  /** How many of the selected drafts have been written so far. */
  done: number
  total: number
}

export interface CommitOutcome {
  created: Issue[]
  /** Drafts the server refused, with the reason, so the user can fix and retry. */
  failed: Array<{ draft: DraftIssue; message: string }>
}

/**
 * Writes the selected drafts through the EXISTING create endpoint.
 *
 * Sequential rather than parallel, for two reasons: issue keys are allocated
 * from a per-project counter that serialises anyway, and a partial failure
 * halfway through a batch is far easier to report when the order is known.
 * A failure does not abort the rest — nine good issues should not be lost
 * because the tenth had a title the server disliked.
 */
export function useCommitDrafts(slug: string | null, projectKey: string | null) {
  const client = useQueryClient()
  const [progress, setProgress] = useState<CommitProgress>({ done: 0, total: 0 })

  const mutation = useMutation<CommitOutcome, unknown, { drafts: DraftIssue[]; sprintId?: UUID | null }>({
    mutationFn: async ({ drafts, sprintId }) => {
      const outcome: CommitOutcome = { created: [], failed: [] }
      setProgress({ done: 0, total: drafts.length })

      for (const draft of drafts) {
        const input: IssueWriteInput = {
          title: draft.title.trim(),
          type: draft.issue_type,
          priority: draft.priority,
        }
        if (draft.description.trim()) input.description = draft.description.trim()
        if (draft.story_points !== null && Number.isFinite(draft.story_points)) {
          input.story_points = draft.story_points
        }
        if (draft.labels.length > 0) input.labels = draft.labels
        if (sprintId) input.sprint_id = sprintId

        try {
          outcome.created.push(await v2Api.createIssue(slug as string, projectKey as string, input))
        } catch (error) {
          outcome.failed.push({
            draft,
            message: error instanceof ApiError ? error.message : 'Could not create this issue.',
          })
        }
        setProgress((current) => ({ ...current, done: current.done + 1 }))
      }
      return outcome
    },
    onSettled: () => {
      if (!slug) return
      // Anything created has to appear on the board and in the backlog now,
      // not on the next navigation.
      invalidateIssueLists(client, slug)
      void client.invalidateQueries({ queryKey: issueKeys.labels(slug) })
    },
  })

  const reset = () => {
    setProgress({ done: 0, total: 0 })
    mutation.reset()
  }

  return { ...mutation, progress, reset }
}

/** Count options for the picker: small numbers people actually plan in. */
export function useCountOptions(): number[] {
  // Starts at 1. "Draft me one issue from this paragraph" is a normal thing to
  // want — a single ticket out of a bug report or a Slack message — and the
  // list used to begin at 3, so the smallest possible ask was three times
  // bigger than the job. The server already clamps to [1, MaxCount], so 1 needs
  // nothing behind it.
  return useMemo(() => [1, 2, 3, 5, 8, 10, AI_MAX_COUNT], [])
}
